(ns app.front.view.workspace.archival-on-finished
  "Confirm modal content when the `Set as Finished` button is clicked on the execution phase
  and the user is prompted to archive the workspace during the operation as well.

  The common confirm signal is used to send the data to the backend and the signal itself is changed
  from `:process.signal/set-finished` to `:process.signal/finish-and-archive` if the user chooses to archive.
  This is done to avoid the FSM state transition limitations where the signal itself has to know the next state"
  (:require [app.domain.archiving.model :as archiving-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag]]
            [app.front.view.archiving.components :as arch-comp]
            [app.front.view.workspace.ecm-sync-on-finished :as ecm-sync-on-finished]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(defn get-archive-on-finish-config
  "Returns the flag stating whether archiving is required, optional or not applicable.
  Note that if archiving is not enabled then not applicable is returned, regardless of the configured value"
  [workspace]
  (let [auth-group (:domain/authority-group workspace)]
    (or (when (or (-> auth-group :group/features :group.feature/archiving not)
                  (not (config-model/resolve-boolean :group.config/archiving-enabled? workspace)))
          :group.config.archive-on-finish/not-applicable)
        (-> (:workspace/category-group-config workspace)
            :group.config/archive-on-finish)
        config-model/default-archive-on-finish)))


(defn- get-confirm-signal-parameters
  "Processes the metadata into a format accepted by `process.signal/set-finished`
  Identifies the fields by schema id since the actual field entities do not exist yet"
  [metadata]
  (update metadata
          :archiving-fields
          (comp (partial map (fn [{:archiving-field/keys [value selected-value schema]}]
                               (util/mmap {:schema-id (:archiving-field-schema/id schema)}
                                          selected-value
                                          value)))
                archiving-model/filter-archiving-fields)))

(defn valid-confirm-signal-parameters?
  "Returns true when the user can click `Yes` on the confirmation dialog"
  [params]
  (->> (:archiving-fields params)
       (every? (fn [{:keys [selected-value value]}]
                 (and (some? value)
                      ;  Manual input exists (value != selected-value) when required
                      (not (and (archiving-model/manual-input-is-visible? selected-value)
                                (= value selected-value))))))))


(rf/reg-event-fx ::get-default-metadata
  (fn [{:keys [db]} [_ fsm-target]]
    (let [workspace    (:entity/parent fsm-target)
          workspace-id (:workspace/id workspace)
          config-flag  (get-archive-on-finish-config workspace)]
      (if (not= config-flag :group.config.archive-on-finish/not-applicable)
        {:db           (-> db
                           (p/dissoc-in [::metadata workspace-id])
                           (assoc ::config-flag config-flag)
                           (assoc ::will-archive? (= config-flag :group.config.archive-on-finish/required)))
         :comm/command {:command    :archiving/get-metadata-values
                        :data       {:workspace-id workspace-id}
                        :on-success [::get-default-metadata.success workspace-id]}}
        {:db (assoc db ::config-flag config-flag)}))))


(rf/reg-event-fx ::get-default-metadata.success
  (fn [{:keys [db]} [_ workspace-id _ metadata]]
    (p/assoc-if
      {:db (assoc-in db [::metadata workspace-id] metadata)}
      :dispatch (when (::will-archive? db)
                  [:app.front.view.workspace.primary-actions/set-confirm-signal-parameters
                   (get-confirm-signal-parameters metadata)]))))


(rf/reg-event-fx ::toggle-archiving
  ;; Signal changes because FSM's monopoly on state transitions requires a signal to know its next state beforehand
  (fn [{:keys [db]} [_ workspace-id]]
    {:db       (update db ::will-archive? not)
     :dispatch [:app.front.view.workspace.primary-actions/set-confirm-signal
                (if (::will-archive? db)
                  [:process.signal/set-finished]
                  [:process.signal/finish-and-archive (->> (get-in db [::metadata workspace-id])
                                                           get-confirm-signal-parameters)])]}))


(rf/reg-event-db ::replace-metadata
  (fn [db [_ workspace-id new-metadata]]
    (assoc-in db [::metadata workspace-id] new-metadata)))


(rf/reg-sub ::metadata
  (fn [db [_ workspace-id]]
    (get-in db [::metadata workspace-id])))


;; Can't check if user has archivist auths since archiving phase may not exist yet
(rf/reg-sub ::user-is-archivist?
  :<- [:session/authority-groups]
  :<- [::ws-subs/workspace]
  (fn [[auth-groups workspace] _]
    (some-> auth-groups
            (p/find-first {:db/id (-> workspace :domain/authority-group :db/id)})
            :group-roles
            (contains? :group.role/archivist))))


(rf/reg-sub ::will-archive?
  #(-> % ::will-archive? boolean))


(rf/reg-sub ::config-flag
  :-> ::config-flag)


(defn- update-metadata
  "Takes the current metadata and updates the value while also saving it in the confirm signal parameters"
  [workspace-id confirm-signal-on-change old-metadata field-id value]
  (let [new-metadata (if (= :sensitivity-level field-id)
                       (assoc old-metadata :sensitivity-level value)
                       (update old-metadata
                               :archiving-fields
                               #(archiving-model/filter-archiving-fields
                                  (map (fn [{{:archiving-field-schema/keys [id possible-values]} :archiving-field/schema
                                             :as                                                 field}]
                                         ;; Same on-change handler handles both dropdowns and manually typed inputs
                                         (if (= id field-id)
                                           (if (or (= value :archiving/manual-input)
                                                   (contains? (set possible-values) value))
                                             (-> field
                                                 (assoc :archiving-field/selected-value value)
                                                 (assoc :archiving-field/value value))
                                             (assoc field :archiving-field/value value))
                                           field))
                                       %))))]
    (>evt [::replace-metadata workspace-id new-metadata])
    (confirm-signal-on-change (get-confirm-signal-parameters new-metadata))))


(defn- sensitivity-level-editor [{:keys [tr disabled? sensitivity-level workspace-id update-field]}]
  [ui-select/select {:id           (str "sensitivity-level-finishing-" workspace-id)
                     :values       workspace-model/workspace-sensitivity-values
                     :val->str     #(some-> % vector tr)
                     :allow-empty? false
                     :label-text   (tr [:workspace/sensitivity-level])
                     :required     true
                     :disabled     disabled?
                     :on-change    #(update-field :sensitivity-level %)
                     :value        sensitivity-level}])


(defn- archiving-fields-editor
  [{:keys [tr disabled? archiving-fields update-field]}]
  (for-frag [{:archiving-field/keys                          [value selected-value schema]
              {:archiving-field-schema/keys [id field-name]} :archiving-field/schema} archiving-fields]
    [:div.flex.flex-row.space-x-16
     (arch-comp/archiving-field-input
       schema
       (util/mmap
         {:id        (str "archiving-field-" id)
          :on-change #(update-field id %)
          :label     field-name
          :required  true
          :disabled  disabled?}
         tr value selected-value))]))


(defn metadata-editor
  "Allows user to edit the metadata before finishing and archiving the workspace
  The `on-change` prop is used to update the confirmed signal parameters"
  [{:keys                                                     [tr on-change]
    {workspace-id :workspace/id}                              :workspace
    {:keys [sensitivity-level archiving-fields] :as metadata} :metadata}]
  (let [update-field     (partial update-metadata workspace-id on-change metadata)
        disabled?        (not (<sub [::user-is-archivist?]))
        archiving-fields (->> archiving-fields
                              archiving-model/filter-archiving-fields
                              (sort-by (comp :archiving-field-schema/id :archiving-field/schema)))
        opts             (util/mmap tr disabled? workspace-id sensitivity-level archiving-fields update-field)
        will-archive?    (<sub [::will-archive?])
        config-flag      (<sub [::config-flag])]
    [:<>
     (when (= config-flag :group.config.archive-on-finish/optional)
       (cc/checkbox
         {:id         (str "archive-on-submit-checkbox-" workspace-id)
          :class-name "mb-4"
          :checked    will-archive?
          :disabled   (not (<sub [::user-is-archivist?]))
          :label-text (tr [:archiving/archive-on-finish])
          :title      (tr [:archiving/archive-on-finish])
          :on-change  #(>evt [::toggle-archiving workspace-id])}))
     (when-not (<sub [::user-is-archivist?])
       [ui/help (tr [:archiving/not-archivist])])
     (when will-archive?
       [:<>
        [:h3.mb-4 (tr [:archiving/metadata-for-archival])]
        [sensitivity-level-editor opts]
        [archiving-fields-editor opts]])]))


(defn confirm-content
  "Contents for the confirmation modal 'action-parameters' when clicking `Set as Finished`"
  [{:keys [tr on-change process-target]}]
  (let [workspace   (<sub [::ws-subs/workspace])
        metadata    (<sub [::metadata (:workspace/id workspace)])
        config-flag (<sub [::config-flag])]
    [:<>
     [ecm-sync-on-finished/ecm-sync-confirm (util/mmap tr)]
     (when (and (not= config-flag :group.config.archive-on-finish/not-applicable)
                (seq (<sub [::process/signals-available process-target [[:process.signal/finish-and-archive]]])))
       (when metadata
         [eb/error-boundary
          [metadata-editor (util/mmap metadata on-change tr workspace)]]))]))
